// fileParser.ts
export interface ParsedFile {
    name: string;
    content: string;
}

// --- helper to extract balanced JSON ---
export function extractBalancedJson(block: string): string | null {
    const start = block.indexOf("{");
    if (start === -1) return null;

    let depth = 0;
    let inString = false;
    let escaped = false;

    for (let i = start; i < block.length; i++) {
        const ch = block[i];

        if (inString) {
            if (escaped) {
                escaped = false;
            } else if (ch === "\\") {
                escaped = true;
            } else if (ch === '"') {
                inString = false;
            }
            continue;
        }

        if (ch === '"') {
            inString = true;
            continue;
        }
        if (ch === "{") depth++;
        if (ch === "}") {
            depth--;
            if (depth === 0) {
                return block.slice(start, i + 1).trim();
            }
        }
    }
    return null; // unbalanced
}

// --- main parser ---
export function parseFilesFromMessage(text: string) {

    const files: ParsedFile[] = [];

    const fileBlockRegex = /^:FileStarts:\s*(.+)\s*[\r\n]+([\s\S]*?)\s*:FileEnds:/gm;

    let match: RegExpExecArray | null;

    while ((match = fileBlockRegex.exec(text)) !== null) {
        const [, name, block] = match;

        let content: string;
        try {
            content = JSON.stringify(JSON.parse(block), null, 2);
        } catch {
            content = block.trim();
        }

        files.push({
            name: name.trim(),
            content,
        });
    }

    const cleanText = (text ?? "").replace(fileBlockRegex, "").trim();

    return { files, cleanText };
}

// --- document type detection ---
export type DetectedDocType =
    | { kind: 'pdf' }
    | { kind: 'edi'; subtype?: '835' | '837' | '834' | '277' | '271' | '274' | '276' | '279' | '810' }
    | { kind: 'fhir' }
    | { kind: 'json' }
    | { kind: 'unknown' };

// Common EDI file extensions (shared by chat accept list and isEdiFile)
export const EDI_FILE_ACCEPT_EXTENSIONS = [
    '.edi', '.dat', '.x12',
    // Transaction-specific extensions
    '.271', '.274',           // Eligibility, Provider Info
    '.276', '.277', '.279',   // Claim Status Request, Response, Review
    '.810',                   // Invoice
    '.834',                   // Benefit Enrollment
    '.835',                   // Payment/Remittance
    '.837', '.837i', '.837p', '.837d', '.837cln',  // Claims (Institutional, Professional, Dental)
] as const;

const EDI_EXTENSIONS = EDI_FILE_ACCEPT_EXTENSIONS;

/**
 * Detect if a file is an EDI file based on its extension.
 * EDI files can have:
 * - Common EDI extensions: .edi, .dat, .x12
 * - Transaction-specific extensions: .271, .274, .276, .277, .279, .810, .834, .835, .837, .837i, .837p, .837d
 */
export function isEdiFile(filename?: string): boolean {
    if (!filename) return false;

    const lowerName = filename.toLowerCase();
    const fileExtension = lowerName.substring(lowerName.lastIndexOf('.'));

    // Check for common EDI extensions
    if (EDI_EXTENSIONS.some((ext) => ext === fileExtension)) return true;

    // Check for numeric EDI transaction extensions
    if (/\.(271|274|276|277|279|810|834|835|837[ipd]?)$/i.test(lowerName)) return true;

    return false;
}

// Heuristics-based detection from file name + content
export function detectDocumentType(name?: string, content?: string): DetectedDocType {
    const lowerName = (name || '').toLowerCase();
    if (lowerName.endsWith('.pdf')) return { kind: 'pdf' };
    // File-name hints for X12 transactions
    if (/\b271\b/.test(lowerName) || lowerName.endsWith('.271')) return { kind: 'edi', subtype: '271' };
    if (/\b274\b/.test(lowerName) || lowerName.endsWith('.274')) return { kind: 'edi', subtype: '274' };
    if (/\b276\b/.test(lowerName) || lowerName.endsWith('.276')) return { kind: 'edi', subtype: '276' };
    if (/\b277\b/.test(lowerName) || lowerName.endsWith('.277')) return { kind: 'edi', subtype: '277' };
    if (/\b279\b/.test(lowerName) || lowerName.endsWith('.279')) return { kind: 'edi', subtype: '279' };
    if (/\b810\b/.test(lowerName) || lowerName.endsWith('.810')) return { kind: 'edi', subtype: '810' };
    if (/\b834\b/.test(lowerName) || lowerName.endsWith('.834')) return { kind: 'edi', subtype: '834' };
    if (/\b835\b/.test(lowerName) || lowerName.endsWith('.835')) return { kind: 'edi', subtype: '835' };
    if (/\b837[ipd]?\b/.test(lowerName) || /\.837[ipd]?$/.test(lowerName)) return { kind: 'edi', subtype: '837' };

    const text = (content || '').trim();
    // Basic EDI delimiter and segment markers
    if (/^isa\*/i.test(text) || text.includes('~se*') || /\*gs\*/i.test(text)) {
        // quick transaction type detection from ST segment
        if (/\*st\*271\*/i.test(text)) return { kind: 'edi', subtype: '271' };
        if (/\*st\*274\*/i.test(text)) return { kind: 'edi', subtype: '274' };
        if (/\*st\*276\*/i.test(text)) return { kind: 'edi', subtype: '276' };
        if (/\*st\*277\*/i.test(text)) return { kind: 'edi', subtype: '277' };
        if (/\*st\*279\*/i.test(text)) return { kind: 'edi', subtype: '279' };
        if (/\*st\*810\*/i.test(text)) return { kind: 'edi', subtype: '810' };
        if (/\*st\*834\*/i.test(text)) return { kind: 'edi', subtype: '834' };
        if (/\*st\*835\*/i.test(text)) return { kind: 'edi', subtype: '835' };
        if (/\*st\*837\*/i.test(text)) return { kind: 'edi', subtype: '837' };
        return { kind: 'edi' };
    }

    // JSON and FHIR detection
    try {
        const parsed = JSON.parse(text);
        if (parsed && (parsed.resourceType || parsed.entry || parsed.Patient)) {
            return { kind: 'fhir' };
        }
        // JSON that represents X12 (post-parsed)
        const hasX12Headers =
            parsed?.transaction_set_header || parsed?.ISA || parsed?.isa || parsed?.GS || parsed?.ST || parsed?.st || parsed?.functional_group_header;
        const has835Hints = parsed?.remittanceSummary || parsed?.summary || parsed?.claim_adjustment || parsed?.payment_information;
        const has837Hints = parsed?.claim_information || parsed?.billing_provider || parsed?.subscriber || parsed?.payer;
        if (hasX12Headers && has835Hints) return { kind: 'edi', subtype: '835' };
        if (hasX12Headers && has837Hints) return { kind: 'edi', subtype: '837' };
        if (hasX12Headers && (parsed?.ST?.[1] === '835')) return { kind: 'edi', subtype: '835' };
        if (hasX12Headers && (parsed?.ST?.[1] === '837')) return { kind: 'edi', subtype: '837' };
        return { kind: 'json' };
    } catch {
        // not JSON
    }
    return { kind: 'unknown' };
}

// Default export for the fileParser module
const fileParser = {
    parseFilesFromMessage,
    detectDocumentType,
    extractBalancedJson,
    isEdiFile,
};

export default fileParser;
